import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepButton,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AddRounded from "@mui/icons-material/AddRounded";
import ForumRounded from "@mui/icons-material/ForumRounded";
import HelpRounded from "@mui/icons-material/HelpRounded";
import { useNavigate } from "react-router-dom";
import ChartMindHelpDialog from "./ChartMindHelpDialog";

const STEPS = {
  RECORD: "record",
  DIAGNOSIS: "diagnosis",
  DIAGNOSTIC_PLAN: "diagnostic_plan",
  CONFIRM_DIAGNOSIS: "confirm_diagnosis",
  TREATMENT: "treatment",
  DISCHARGE: "discharge",
  CHART: "chart",
};

// Step order for progressive unlocking
const STEP_ORDER = [
  STEPS.RECORD,
  STEPS.DIAGNOSIS,
  STEPS.DIAGNOSTIC_PLAN,
  STEPS.CONFIRM_DIAGNOSIS,
  STEPS.TREATMENT,
  STEPS.DISCHARGE,
  STEPS.CHART,
];

const ChartMindHeader = ({
  currentStep,
  setCurrentStep,
  isRecording,
  recordingTime,
  ddxLoading,
  hasDiagnoses,
  diagnosticPlanLoading,
  hasDiagnosticPlan,
  treatmentPlanLoading,
  hasTreatmentPlan,
  chartLoading,
  hasNote,
  hasTranscript,
  hasSelectedDiagnosis,
  visitedSteps,
  onNewEncounter = undefined,
  hasEncounterData = false,
  // [BETA-CM0427] When true, hides Plan + Confirm steps and renames Treatment → "Plan". Remove when beta ends.
  cm0427Mode = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);
  const [helpOpen, setHelpOpen] = useState(false);

  // On mobile, keep the active step centered in the scroll container
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || container.scrollWidth <= container.clientWidth) return;
    // Skip on desktop — only scroll when there's actual overflow (mobile)
    if (window.innerWidth >= theme.breakpoints.values.sm) return;
    const steps = container.querySelectorAll(".MuiStep-root");
    const activeEl = steps[getActiveStepIndex()];
    if (!activeEl) return;
    const elCenter = activeEl.offsetLeft + activeEl.offsetWidth / 2;
    container.scrollTo({
      left: elCenter - container.offsetWidth / 2,
      behavior: "smooth",
    });
  }, [currentStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // COMMENTED OUT: Status indicators (recording/loading/ready states)
  // Determine what status to show (priority: chart > treatment > plan > ddx)
  // const showChartStatus = !isRecording && (chartLoading || hasNote);
  // const showTreatmentStatus = !isRecording && !showChartStatus && (treatmentPlanLoading || hasTreatmentPlan);
  // const showPlanStatus = !isRecording && !showChartStatus && !showTreatmentStatus && (diagnosticPlanLoading || hasDiagnosticPlan);
  // const showDdxStatus = !isRecording && !showChartStatus && !showTreatmentStatus && !showPlanStatus && (ddxLoading || hasDiagnoses);

  // const formatTime = (seconds) => {
  //   const mins = Math.floor(seconds / 60);
  //   const secs = seconds % 60;
  //   return `${mins.toString().padStart(2, "0")}:${secs
  //     .toString()
  //     .padStart(2, "0")}`;
  // };

  // [BETA-CM0427] Filter out Plan + Confirm steps when CM0427 mode is enabled.
  const visibleStepOrder = cm0427Mode
    ? STEP_ORDER.filter(
        (s) => s !== STEPS.DIAGNOSTIC_PLAN && s !== STEPS.CONFIRM_DIAGNOSIS,
      )
    : STEP_ORDER;

  // Map step values to indices for Stepper
  const getActiveStepIndex = () => {
    return visibleStepOrder.indexOf(currentStep);
  };

  const handleStepClick = (step) => {
    if (!isStepDisabled(step)) {
      setCurrentStep(step);
    }
  };

  // Helper: Check if a step should be disabled
  // Only allow navigating to steps that have been visited via "Next" button
  const isStepDisabled = (step) => {
    // Recording is always available
    if (step === STEPS.RECORD) return false;

    // Diagnosis requires transcript and must be visited
    if (step === STEPS.DIAGNOSIS) {
      return !hasTranscript || !visitedSteps.has(STEPS.DIAGNOSIS);
    }

    // Chart can be skipped to from anywhere as long as there's a transcript —
    // the Chart step itself offers a "Generate Note" button for early generation.
    if (step === STEPS.CHART) {
      return !hasTranscript;
    }

    // All other steps: must have been visited via Next button
    return !visitedSteps.has(step);
  };

  // Step labels — keyed by step so filtering stays consistent.
  // [BETA-CM0427] In CM0427 mode, Treatment is relabeled "Plan" (since the diagnostic Plan step is hidden).
  const stepLabelMap = {
    [STEPS.RECORD]: "Recording",
    [STEPS.DIAGNOSIS]: "Diagnosis",
    [STEPS.DIAGNOSTIC_PLAN]: "Plan",
    [STEPS.CONFIRM_DIAGNOSIS]: "Confirm",
    [STEPS.TREATMENT]: cm0427Mode ? "Plan" : "Treatment",
    [STEPS.DISCHARGE]: "Discharge",
    [STEPS.CHART]: "Chart",
  };

  return (
    <>
      <Box
        sx={{
          ...theme.mixins.toolbar,
          width: "100%",
          display: "flex",
          alignItems: "center",
          pl: 2,
          pr: 2,
          flexShrink: 0,
        }}
      >
        {/* Stepper navigation — horizontally scrollable on mobile */}
        <Box
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            minWidth: 0,
            overflowX: { xs: "auto", sm: "auto" },
            overflowY: { xs: "hidden", sm: "visible" },
            scrollbarWidth: "thin",
            scrollbarColor: `${theme.palette.divider} transparent`,
            "&::-webkit-scrollbar": { height: 4 },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 2,
              backgroundColor: theme.palette.divider,
            },
            "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
            [theme.breakpoints.up("sm")]: {
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
              alignSelf: "stretch",
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          <Stepper
            activeStep={getActiveStepIndex()}
            nonLinear
            sx={{
              minWidth: { xs: 520, sm: "unset" },
              maxWidth: { sm: 720 },
              "& .MuiStepLabel-label": { fontWeight: 700 },
              "& .MuiStepLabel-label.Mui-active": { color: "primary.main" },
            }}
          >
            {visibleStepOrder.map((step, index) => {
              const nextStep = visibleStepOrder[index + 1];
              const isCompleted = nextStep && visitedSteps.has(nextStep);
              const disabled = isStepDisabled(step);
              const isActive = index === getActiveStepIndex();

              const stepIconColor = disabled
                ? theme.palette.action.disabled
                : isActive || !isCompleted
                  ? theme.palette.primary.main
                  : undefined;

              return (
                <Step
                  key={step}
                  completed={isCompleted}
                  sx={{
                    "& .MuiStepIcon-root": {
                      ...(stepIconColor &&
                        !isCompleted && { color: stepIconColor }),
                    },
                  }}
                >
                  <StepButton
                    onClick={() => handleStepClick(step)}
                    disabled={disabled}
                  >
                    {stepLabelMap[step]}
                  </StepButton>
                </Step>
              );
            })}
          </Stepper>
        </Box>

        {/* Right: Help + PeerView + New Encounter (desktop only — floats on mobile) */}
        {!isMobile && (
          <Box sx={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Help">
              <IconButton size="small" onClick={() => setHelpOpen(true)} sx={{ color: "text.secondary" }}>
                <HelpRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="PeerView Consultation">
              <IconButton size="small" onClick={() => navigate("/dashboard/peerview")} sx={{ color: "text.secondary" }}>
                <ForumRounded fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="New Encounter">
              <IconButton size="small" onClick={onNewEncounter} sx={{ color: "text.secondary" }}>
                <AddRounded fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Floating buttons on mobile */}
      {isMobile && (
        <Box sx={{ position: "fixed", top: 12, right: 16, zIndex: 1150, display: "flex", gap: 0.5 }}>
          <Tooltip title="Help">
            <IconButton size="small" onClick={() => setHelpOpen(true)} sx={{ color: "text.secondary" }}>
              <HelpRounded fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="PeerView Consultation">
            <IconButton size="small" onClick={() => navigate("/dashboard/peerview")} sx={{ color: "text.secondary" }}>
              <ForumRounded fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="New Encounter">
            <IconButton size="small" onClick={onNewEncounter} sx={{ color: "text.secondary" }}>
              <AddRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <ChartMindHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export { STEPS };
export default ChartMindHeader;
